/* eslint-disable camelcase */
import { MOBILE_SCORE_ROLLUP_VALUE } from 'src/apps/dashboard/constants/MobileDashboard.constants';
import {
	ScoreData,
	ScoreDataResult
} from 'src/apps/dashboard/services/queries/score/helpers/transformResults';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	calculateRecentPeriodDates,
	formatDateForGraphQL
} from 'src/common/helpers/DateFormatHelper';
import { ConditionString } from 'src/common/types/graphql.generated';

import { MobileScoreDataQuery, MobileScoreDataQueryVariables } from '../MobileScoreData.generated';

import { getMobileAppIdsFromFilters, getPlatformFromFilters } from './mobileFilterVariableBuilder';

type ScoreRow = NonNullable<NonNullable<MobileScoreDataQuery['mobileScore']>[number]>;

const buildUnitIdFilter = (hasFilter: boolean, mobileBuIds?: string[]): ConditionString => {
	if (hasFilter && mobileBuIds?.length) {
		return { value: mobileBuIds };
	}
	return { value: [MOBILE_SCORE_ROLLUP_VALUE] };
};

export const buildMobileScoreDataVariables = (
	days: number,
	filters?: IFilterOptions,
	mobileBuIds?: string[]
): MobileScoreDataQueryVariables => {
	const { today: todayDate, recentPeriodStart } = calculateRecentPeriodDates(days);
	const platform = getPlatformFromFilters(filters);
	const appIds = getMobileAppIdsFromFilters(filters);
	const hasFilter = platform !== 'all' || appIds.length > 0;

	return {
		days,
		calculationDateFrom: { value: [formatDateForGraphQL(recentPeriodStart)] },
		calculationDateTo: { value: [formatDateForGraphQL(todayDate)] },
		platformFilter:
			platform === 'all' && appIds.length
				? undefined
				: { value: [platform === 'all' ? MOBILE_SCORE_ROLLUP_VALUE : platform] },
		appIdFilter: { value: appIds.length ? appIds : [MOBILE_SCORE_ROLLUP_VALUE] },
		unitIdFilter: buildUnitIdFilter(hasFilter, mobileBuIds)
	};
};

// Aggregate per-BU score rows by date using weighted average (weight = unique_issues).
const aggregateScoresByDate = (products: ScoreRow[]): ScoreData[] => {
	const dateMap = new Map<string, { totalWeight: number; weightedScore: number }>();

	products.forEach((item) => {
		const date = item.calculation_date;
		const weight = item.unique_issues ?? 1;
		const existing = dateMap.get(date) ?? { totalWeight: 0, weightedScore: 0 };
		dateMap.set(date, {
			totalWeight: existing.totalWeight + weight,
			weightedScore: existing.weightedScore + item.score * weight
		});
	});

	return Array.from(dateMap.entries())
		.map(([date, { totalWeight, weightedScore }]) => ({
			calculation_date: date,
			unit_id: null,
			environment: null,
			score: Math.round(
				totalWeight > 0 ? Math.min(Math.max((weightedScore / totalWeight) * 100, 0), 100) : 0
			),
			timeframe_days: null
		}))
		.sort(
			(a, b) => new Date(a.calculation_date).getTime() - new Date(b.calculation_date).getTime()
		);
};

export const transformMobileScoreDataResults = (
	rawResults: MobileScoreDataQuery
): ScoreDataResult => {
	const scoreProducts = (rawResults.mobileScore ?? []).filter(
		(item): item is ScoreRow => item !== null
	);

	const scores = aggregateScoresByDate(scoreProducts);

	const lastUpdatedAt =
		scores.length > 0 ? new Date(scores[scores.length - 1].calculation_date).getTime() : 0;

	return {
		scores,
		lastUpdatedAt
	};
};
